import React, { FC, useEffect, useState } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers";
import { addYears, format, isValid, parse } from "date-fns";
import TranslationWrapper from "../../old/beheer/components/TranslationWrapper/TranslationWrapper.tsx";
import { TimeRangePicker } from "./TimeRangePicker.tsx";
import { TimeRangeMethods, toTimeRange, Time } from "@jamezz/react-components";
import _ from "lodash";
import { OpeningHoursForDay2 } from "./OpeningHoursSettings.tsx";
import { v7 } from "uuid";
import AddIcon from "@mui/icons-material/Add";
import { TimeTableExtensionProps } from "./TimeTable.tsx";

type AddExceptionDialogProps<T> = {
  date?: string;
  openingHours?: OpeningHoursForDay2<T>[];
  yearly?: boolean;
  isOpened?: boolean;
  open: boolean;
  onSave: (date: string, timeRanges: OpeningHoursForDay2<T>[]) => void;
  onClose: () => void;
  defaultData: T;
  ExtensionComponent?: FC<TimeTableExtensionProps<T>>;
};

export const AddEditExceptionDialog = <T,>({
  yearly = false,
  isOpened = false,
  open,
  onSave,
  onClose,
  defaultData,
  openingHours,
  date,
  ExtensionComponent,
}: AddExceptionDialogProps<T>) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeRanges, setSelectedTimeRanges] = useState<OpeningHoursForDay2<T>[]>([]);
  const [isYearlyChecked, setIsYearlyChecked] = useState(yearly);
  const [selectedIsOpened, setSelectedIsOpened] = useState(isOpened);

  useEffect(() => {
    if (date) {
      let receivedDate = parse(date, "yyyy-MM-dd", new Date());
      setIsYearlyChecked(false);
      if (!isValid(receivedDate)) {
        receivedDate = parse(date, "MM-dd", new Date());
        setIsYearlyChecked(true);
      }

      setSelectedDate(receivedDate);
    }
  }, [date]);

  useEffect(() => {
    setSelectedIsOpened(false);
    if (openingHours) {
      setSelectedTimeRanges(openingHours);
      if (openingHours.length > 0) setSelectedIsOpened(true);
    }
  }, [openingHours]);

  const handleClose = () => {
    setSelectedDate(new Date());
    setIsYearlyChecked(false);
    setSelectedTimeRanges([]);

    onClose();
  };

  return (
    <Dialog open={open} keepMounted={false}>
      <DialogTitle sx={{ m: 0, p: 2 }} id={"customized-dialog-title"}>
        <Typography>
          <TranslationWrapper tKey={"Exceptions of schema"} tDefault={"Exceptions of schema"} />
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label={"close"}
        onClick={handleClose}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Stack sx={{ padding: 3 }} alignItems={"flex-start"}>
          <FormControl component={"fieldset"}>
            <FormLabel component={"legend"}>
              <TranslationWrapper tKey={"AddExceptionDialog.Select Date"} tDefault={"Select Date"} />
            </FormLabel>
            <DatePicker
              openTo={"day"}
              minDate={new Date()}
              maxDate={addYears(new Date(), 5)}
              value={selectedDate}
              onChange={(value) => {
                if (value !== null) {
                  setSelectedDate(value);
                }
              }}
            />
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={isYearlyChecked}
                onChange={() => setIsYearlyChecked(!isYearlyChecked)}
                name={"yearly"}
              />
            }
            label={<TranslationWrapper tKey={"Yearly"} tDefault={"Yearly"} />}
          />
          <FormControlLabel
            control={
              <Switch
                checked={selectedIsOpened}
                onChange={() => {
                  if (!selectedIsOpened) {
                    setSelectedTimeRanges([
                      ...selectedTimeRanges,
                      { hours: "12:00-18:00", id: v7(), data: defaultData },
                    ]);
                  } else {
                    setSelectedTimeRanges([]);
                  }

                  setSelectedIsOpened(!selectedIsOpened);
                }}
              />
            }
            label={<TranslationWrapper tKey={"open"} tDefault={"Open"} />}
          />
          <FormGroup>
            <FormLabel>Time slots</FormLabel>
            {selectedTimeRanges.map((openingHoursForDay) => {
              const timeRange = toTimeRange(openingHoursForDay.hours);

              return (
                <TimeRangePicker
                  key={openingHoursForDay.id}
                  hideDeleteButton={selectedTimeRanges.length <= 1}
                  timeRange={timeRange}
                  onChange={(newTimeRange) => {
                    const newTimes = _.cloneDeep(selectedTimeRanges);
                    const index = newTimes.findIndex((item) => item.id === openingHoursForDay.id);
                    if (newTimeRange) {
                      newTimes[index] = { ...newTimes[index], hours: TimeRangeMethods.timeRangeToString(newTimeRange) };
                    } else {
                      newTimes.splice(index, 1);
                    }
                    setSelectedTimeRanges(newTimes);
                  }}
                  ExtensionComponent={
                    ExtensionComponent !== undefined ? (
                      <ExtensionComponent
                        openingHours={openingHoursForDay}
                        data={openingHoursForDay.data}
                        defaultValue={defaultData}
                        onChange={(data) => {
                          setSelectedTimeRanges(
                            selectedTimeRanges.map((timeRange) => {
                              if (timeRange.id === data.id) {
                                return data;
                              }

                              return timeRange;
                            })
                          );
                        }}
                      />
                    ) : undefined
                  }
                />
              );
            })}
          </FormGroup>
          <IconButton
            onClick={() => {
              const openingHoursForDay = _.last(selectedTimeRanges ?? []);
              let start: Time = { hours: 12, minutes: 0 };
              if (openingHoursForDay) {
                start = toTimeRange(openingHoursForDay.hours).end;
              }

              setSelectedTimeRanges([
                ...selectedTimeRanges,
                {
                  hours: TimeRangeMethods.timeRangeToString({ start: start, end: { hours: 0, minutes: 0 } }),
                  id: v7(),
                  data: defaultData,
                },
              ]);

              setSelectedIsOpened(true);
            }}
          >
            <AddIcon />
          </IconButton>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          type={"submit"}
          autoFocus
          disabled={selectedDate === null}
          onClick={() => {
            if (selectedDate === null) {
              return;
            }

            let formattedDate = format(selectedDate, "yyyy-MM-dd");
            if (isYearlyChecked) {
              formattedDate = format(selectedDate, "MM-dd");
            }

            onSave(formattedDate, selectedTimeRanges);
            handleClose();
          }}
        >
          <TranslationWrapper tKey={"Save"} tDefault={"Save"} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
